/**
 * ``useDetectionsSseBridge`` — merges the live ``detection``-kind SSE
 * deltas the Map already receives into the shared
 * :func:`detections/detections-store` buffer (REQ-QUERY-028). Detection is
 * the SAPIENT successor to the data-plane Event surface removed in the
 * ADR-032 cascade (PR6).
 *
 * The BFF's SSE stream carries ``status`` / ``detection`` / ``alert`` /
 * ``alert-dismissed`` messages; this bridge picks up the ``detection`` ones
 * so they appear in the Detections panel (and the Map glyph layer) within
 * the sub-second latency Flow 02 requires, ahead of the next REST poll
 * (:func:`detections/use-recent-detections-feed`).
 *
 * Shaped as a prop-driven hook (taking the connection's
 * :type:`SseSubscribe` rather than reading the SSE context) so it composes
 * with the Map's plumbing — the same value threaded into
 * :func:`SnapshotLayer` — and so it unit-tests without a provider: a test
 * passes a two-line fake ``subscribe`` and drives messages directly.
 *
 * Every message on the stream reaches this bridge, including several
 * arriving in one flush (issue #739). It previously read a single
 * ``lastMessage`` slot, which coalesced bursts down to their final message
 * and leaned on the REST poll to backfill the gap — a latency defect
 * against the sub-second paint Flow 02 requires.
 *
 * Cross-references:
 *   - REQ-UI-061 (Detections panel); REQ-UI-006 (live update latency).
 *   - REQ-QUERY-028 (live Detection SSE feed); REQ-INGEST-044 (nullable
 *     position — a null-position detection is valid, not dropped).
 *   - ADR-024 §5; ADR-032; storyboard Flow 02 §"Live update cadence".
 *   - map/sse-subscription.ts (the SseSubscribe fan-out contract).
 */
import { useEffect, useLayoutEffect, useRef } from "react";
import type { SseDetectionPayload, SseMessage } from "@/map/sse-types";
import { useSseMessages, type SseSubscribe } from "@/map/sse-subscription";
import {
  type CommitScheduler,
  type CommitSchedulerOptions,
  createCommitScheduler,
} from "@/map/sse-commit-scheduler";
import { type DetectionRecord, useDetectionsStore } from "@/detections/detections-store";
import {
  type AppendEntry,
  type TrackMember,
  useObjectTrackStore,
} from "@/object-tracks/object-track-store";

/**
 * Map a live :type:`SseDetectionPayload` to the store's
 * :type:`DetectionRecord`.
 *
 * The live NATS feed is a subset of the full ``DetectionItem``: it lacks
 * the ingest-side metadata columns (``source_aggregator_id`` /
 * ``ingress_timestamp`` / ``message_hash``), which the REST feed
 * supplies. Those are filled with empty defaults here — the panel row
 * contract (time / classification / confidence / producer) doesn't read
 * them, and the authoritative values arrive on the next REST poll, whose
 * upsert overwrites this record by ``report_id`` (the metadata-bearing
 * REST row wins last-writer). ``ingress_timestamp`` falls back to the
 * detection ``time`` so the field is never an empty/invalid date.
 */
export function detectionRecordFromSse(payload: SseDetectionPayload): DetectionRecord {
  return {
    report_id: payload.report_id,
    object_id: payload.object_id,
    track_id: payload.track_id ?? "",
    producer_id: payload.producer_id,
    time: payload.time,
    latitude_deg: payload.latitude_deg,
    longitude_deg: payload.longitude_deg,
    elevation_m: payload.elevation_m,
    state: payload.state,
    task_id: payload.task_id,
    detection_confidence: payload.detection_confidence,
    primary_class: payload.primary_class,
    schema_version: payload.schema_version,
    // Not on the live feed — backfilled by the REST poll (see docstring).
    source_aggregator_id: "",
    ingress_timestamp: payload.time,
    message_hash: "",
    source_network: payload.source_network ?? null,
  };
}

/**
 * The member row an SSE detection contributes to its object track (ADR-041;
 * the same detection subset the ``/object-tracks`` backfill carries).
 */
export function trackMemberFromSse(payload: SseDetectionPayload): TrackMember {
  return {
    report_id: payload.report_id,
    time: payload.time,
    latitude_deg: payload.latitude_deg,
    longitude_deg: payload.longitude_deg,
    elevation_m: payload.elevation_m,
    primary_class: payload.primary_class,
    detection_confidence: payload.detection_confidence,
    state: payload.state,
  };
}

/** Options for :func:`useDetectionsSseBridge`. */
export interface DetectionsSseBridgeOptions {
  /** The deployment live window (ms) — the stores' prune floor (REQ-UI-181). */
  readonly retentionMs?: number;
  /**
   * Commit-scheduler knobs (feature spec ``ui-track-load-headroom`` §2.4).
   * Production leaves this undefined (animation frames, 100 ms fallback);
   * tests inject deterministic wakeups.
   */
  readonly commit?: CommitSchedulerOptions;
}

/**
 * Apply one frame's worth of live messages to the two stores: every
 * ``detection`` becomes one detections-buffer row and (when it carries a
 * ``track_id``) one track member, in arrival order, with ONE commit per store
 * (REQ-UI-192). Other kinds are not this bridge's and are skipped. Exported
 * for the perf lane, which times exactly this.
 */
export function applyDetectionBatch(
  batch: readonly SseMessage[],
  retentionMs: number | undefined,
): void {
  const opts = retentionMs === undefined ? undefined : { retentionMs };
  const records: DetectionRecord[] = [];
  const entries: AppendEntry[] = [];
  for (const message of batch) {
    if (message.kind !== "detection") {
      continue;
    }
    records.push(detectionRecordFromSse(message.payload));
    const trackId = message.payload.track_id;
    if (typeof trackId === "string" && trackId.length > 0) {
      entries.push({
        key: {
          track_id: trackId,
          producer_id: message.payload.producer_id,
          object_id: message.payload.object_id,
        },
        member: trackMemberFromSse(message.payload),
      });
    }
  }
  if (records.length > 0) {
    useDetectionsStore.getState().upsertMany(records, opts);
  }
  if (entries.length > 0) {
    useObjectTrackStore.getState().appendMany(entries, opts);
  }
}

/**
 * Mount the live ``detection`` → stores bridge.
 *
 * Each arriving ``detection`` message is queued, in arrival order, on a
 * per-frame commit scheduler; the queue is applied to the detections buffer
 * and the object-track buffer as one batch per animation frame (or within
 * 100 ms when no frame comes — REQ-UI-006 / REQ-UI-192). Nothing is
 * coalesced or dropped: the stores see every event, in order, in fewer
 * commits.
 *
 * One scheduler per mounted hook, created on first use and disposed with the
 * hook, so an unmount never commits into a store after the connection it fed
 * is gone. The live window is read through a ref at commit time, so the
 * deployment config resolving (``retentionMs`` changing once at startup)
 * neither re-creates the scheduler nor drops what it holds.
 */
export function useDetectionsSseBridge(
  subscribe: SseSubscribe | null,
  options: DetectionsSseBridgeOptions = {},
): void {
  const { retentionMs, commit } = options;
  const retentionRef = useRef(retentionMs);
  useLayoutEffect(() => {
    retentionRef.current = retentionMs;
  }, [retentionMs]);
  const commitRef = useRef(commit);
  const schedulerRef = useRef<CommitScheduler | null>(null);
  const scheduler = (): CommitScheduler => {
    schedulerRef.current ??= createCommitScheduler(
      (batch) => applyDetectionBatch(batch, retentionRef.current),
      commitRef.current,
    );
    return schedulerRef.current;
  };
  useEffect(
    () => () => {
      schedulerRef.current?.dispose();
      schedulerRef.current = null;
    },
    [],
  );
  useSseMessages(subscribe, (message) => {
    if (message.kind === "detection") {
      scheduler().enqueue(message);
    }
  });
}
